import React from 'react';
import { uniqBy, flatten } from 'lodash';
import { constants } from '@clubmed/utils';
import { useAppContext } from 'Context/ReactAppContext';
import {
  groupAccommodationsByCategory,
  getTabsItemsAndPropsFromAccommodations,
  withExclusiveCollectionTag,
} from 'Components/lines/product-accommodations-line/productAccommodations.utils';
import type { LineProps } from 'Client/cms/toolkit/product-accommodations/resolver';
import 'Client/cms/toolkit/product-accommodations/resolver';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
const { RANKINGS } = constants;
type BELineProps = LineProps & {
  accommodationCategory?: {
    rankedCategories?: Array<{
      id?: string;
      ranking?: string;
      price?: {
        totalWithoutDiscount?: FormattedPrice;
        total?: FormattedPrice;
        diffPrice?: string;
      };
    }>;
  };
};
type Props = {
  activeIndexes: number;
  formatedPrices: Array<string | null | undefined>;
  product: Product;
  proposals: Array<Proposal>;
  resolvedProposals: Array<Proposal>;
};

const AccommodationsComparator = ({
  activeIndexes,
  formatedPrices,
  product,
  proposals,
  resolvedProposals,
}: Props) => {
  const appContext = useAppContext();
  const displayRoomsComparator = isFeatureFlipped(appContext)('displayRoomsComparator');

  if (!displayRoomsComparator) {
    return null;
  }

  const allAccommodations = uniqBy(
    flatten(
      proposals.map(({ accommodations, price }) => {
        const pricedAccommodations = accommodations;
        return pricedAccommodations.map((acc) => ({ ...acc, price }));
      }),
    ),
    'id',
  );
  const proposalAccommodationCategory = allAccommodations.map(({ categoryId }, index) => ({
    id: categoryId,
    ranking: Object.values(RANKINGS)[index],
  }));
  const props: BELineProps = {
    accommodationCategory: {
      rankedCategories: proposalAccommodationCategory,
    },
    resort: product,
  };
  const { proposalAccommodationById, proposalAccommodationByType } = allAccommodations.reduce(
    (acc, { categoryId, comfortId, exclusiveCollection }) => {
      acc.proposalAccommodationById.push(categoryId);
      acc.proposalAccommodationByType.push(
        withExclusiveCollectionTag({
          categoryId: comfortId,
          isExclusiveCollection: exclusiveCollection,
        }),
      );
      return acc;
    },
    {
      proposalAccommodationById: [],
      proposalAccommodationByType: [],
    },
  );
  const accommodationCategories = groupAccommodationsByCategory(props)
    .filter(
      (category) =>
        category.categoryAccommodations.some((categoryAccommodation) =>
          proposalAccommodationById.includes(categoryAccommodation.accommodation.categoryId),
        ) && proposalAccommodationByType.includes(category.categoryId),
    )
    .sort((current, next) => {
      const currentIndex = proposals.findIndex(
        (_proposal) =>
          current.categoryAccommodations?.[0]?.accommodation.categoryId ===
          _proposal.accommodations?.[0]?.categoryId,
      );
      const nextIndex = proposals.findIndex(
        (_proposal) =>
          next.categoryAccommodations?.[0]?.accommodation.categoryId ===
          _proposal.accommodations?.[0]?.categoryId,
      );
      return currentIndex - nextIndex;
    })
    .map((category) => {
      const proposal = resolvedProposals[category.categoryIndex];
      category.price = {
        initialPrice: proposal?.price.totalWithoutDiscount,
        bestPrice: proposal?.price.total,
        diffPrice: formatedPrices[category.categoryIndex] || '',
      };
      return category;
    });

  if (accommodationCategories.length <= 1) {
    return null;
  }

  const { slot } = getTabsItemsAndPropsFromAccommodations(props, accommodationCategories);
  return <div className="mb-5 ml-auto">{slot([activeIndexes])}</div>;
};

export { AccommodationsComparator };
